/**
 * Sending intents. An action is checked against the predicted view first, so an impossible one
 * never leaves the app; then it joins the pending list (shown at once) and goes to the server.
 */
import { check, type Action, type DevCommand, type Refusal } from '@bramvasterd/terrella-rules';

import { intentKey, type Game } from '../game';
import { predictTime, rebuildView } from '../session/predict';
import type { GameState, Intent } from '../types';

/** The app can't predict (other rules than the server's) and the server hasn't answered yet: buttons wait. */
export const waitingForServer = (s: GameState): boolean => !s.predicting && s.pending.length > 0;

/** Whether an action can happen now, for its button: the rules' own check, so the reason is theirs. */
export type ActionCheck =
  | { state: 'ok'; action: Action }
  | { state: 'refused'; refusal: Refusal }
  /** The server hasn't answered my last change, and the app can't predict: try again in a moment. */
  | { state: 'waiting' };

/** The rules' check of `action` on the predicted view now; with other rules than the server's, the server decides. */
export function actionCheck(game: Game, action: Action): ActionCheck {
  const s = game.store.get();
  if (waitingForServer(s)) return { state: 'waiting' };
  if (!s.predicting || !s.view || !s.me) return { state: 'ok', action };
  const refusal = check(s.view, s.me.id, action, predictTime(game));
  return refusal ? { state: 'refused', refusal } : { state: 'ok', action };
}

/** Queue an action; false, with a refusal event, when the predicted view already says no. */
export function submitAction(game: Game, action: Action): boolean {
  const s = game.store.get();
  if (!s.view || !s.me || !s.world) return false;
  if (s.predicting) {
    const refusal = check(s.view, s.me.id, action, predictTime(game));
    if (refusal) {
      game.events.emit('refused', { intentId: 0, refusal });
      return false;
    }
  }
  queue(game, { id: s.nextIntentId, kind: 'action', action });
  game.events.emit('hop', undefined);
  return true;
}

/** Queue a dev command (skip 6 hours, grow a size, give resources). Never predicted. */
export function runDev(game: Game, command: DevCommand): void {
  const s = game.store.get();
  if (!s.me || !s.world || !s.devTools) return;
  queue(game, { id: s.nextIntentId, kind: 'dev', command });
}

function queue(game: Game, intent: Intent): void {
  const s = game.store.get();
  game.store.set({ pending: [...s.pending, intent], nextIntentId: intent.id + 1 });
  if (s.world)
    game.deps.storage.set(intentKey(s.world.id), String(intent.id)).catch(() => undefined);
  rebuildView(game);
  game.events.emit('intent', intent);
}
